import React, { useRef } from 'react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Camera, Key, CheckCircle2, Loader2 } from 'lucide-react';
import { getPhotoUrl } from '../../services/api';

/**
 * Dialog for capturing the keybox photo at the end of a task.
 * This photo certifies the exact time of task completion.
 */
const KeyboxPhotoDialog = ({
  open,
  onOpenChange,
  keyboxPhoto,
  keyboxPhotoUploading,
  onCapture,
  onConfirm,
  token
}) => {
  const inputRef = useRef(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl flex items-center justify-center gap-2">
            <Key className="h-6 w-6 text-blue-600" />
            Photo de la boîte à clés
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-center text-blue-800 font-medium">
              Prenez une photo des clés dans la boîte à clés
            </p>
            <p className="text-center text-blue-600 text-sm mt-1">
              Cette photo certifie l'heure exacte de fin de prestation
            </p>
          </div>
          
          {keyboxPhoto ? (
            <div className="space-y-3">
              <div className="relative w-full h-48 rounded-lg overflow-hidden bg-gray-100 mx-auto">
                <img 
                  src={getPhotoUrl(keyboxPhoto, token)} 
                  alt="Boîte à clés" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Photo enregistrée
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => inputRef.current?.click()}
                disabled={keyboxPhotoUploading}
              >
                <Camera className="h-4 w-4 mr-2" />
                Reprendre la photo
              </Button>
            </div>
          ) : (
            <div 
              className="border-2 border-dashed border-blue-300 rounded-lg p-8 text-center cursor-pointer hover:bg-blue-50 transition-colors"
              onClick={() => inputRef.current?.click()}
            >
              {keyboxPhotoUploading ? (
                <div className="space-y-2">
                  <Loader2 className="h-12 w-12 mx-auto text-blue-500 animate-spin" />
                  <p className="text-blue-600">Envoi en cours...</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Camera className="h-12 w-12 mx-auto text-blue-400" />
                  <p className="text-blue-600 font-medium">Touchez pour prendre la photo</p>
                  <p className="text-gray-500 text-sm">Les clés doivent être visibles dans la boîte</p>
                </div>
              )}
            </div>
          )}
          
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={onCapture}
            disabled={keyboxPhotoUploading}
          />
        </div>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button 
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
            onClick={onConfirm}
            disabled={!keyboxPhoto || keyboxPhotoUploading}
            data-testid="confirm-keybox-photo-btn"
          >
            {keyboxPhoto ? (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Continuer la validation
              </>
            ) : (
              "Prenez d'abord la photo"
            )}
          </Button>
          <Button 
            variant="ghost" 
            className="w-full text-gray-500"
            onClick={() => onOpenChange(false)}
          >
            Annuler
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default KeyboxPhotoDialog;
